/**
 * Consent Error Page
 *
 * Displays detailed error information when Azure AD admin consent fails.
 * Provides helpful messaging and retry option.
 */

import { useSearchParams, useNavigate } from 'react-router-dom';
import {
    Card,
    CardHeader,
    Text,
    Title2,
    Button,
    makeStyles,
    tokens,
} from '@fluentui/react-components';
import { ErrorCircle24Filled } from '@fluentui/react-icons';

const useStyles = makeStyles({
    container: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: tokens.colorNeutralBackground2,
        padding: tokens.spacingHorizontalXXL,
    },
    card: {
        maxWidth: '520px',
        width: '100%',
        padding: tokens.spacingHorizontalXXL,
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalM,
        marginBottom: tokens.spacingVerticalL,
    },
    errorIcon: {
        color: tokens.colorPaletteRedForeground1,
        fontSize: '32px',
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalM,
    },
    errorCode: {
        fontFamily: tokens.fontFamilyMonospace,
        backgroundColor: tokens.colorNeutralBackground3,
        padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
        borderRadius: tokens.borderRadiusMedium,
        display: 'inline-block',
    },
    errorDescription: {
        backgroundColor: tokens.colorNeutralBackground3,
        padding: tokens.spacingHorizontalM,
        borderRadius: tokens.borderRadiusMedium,
        color: tokens.colorNeutralForeground2,
    },
    buttonContainer: {
        marginTop: tokens.spacingVerticalL,
        display: 'flex',
        gap: tokens.spacingHorizontalM,
    },
    helpText: {
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase200,
        marginTop: tokens.spacingVerticalM,
    },
});

export function ConsentErrorPage(): JSX.Element {
    const styles = useStyles();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const error = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');

    const handleTryAgain = () => {
        navigate('/login');
    };

    return (
        <div className={styles.container}>
            <Card className={styles.card}>
                <CardHeader
                    header={
                        <div className={styles.header}>
                            <ErrorCircle24Filled className={styles.errorIcon} />
                            <Title2>Consent Error</Title2>
                        </div>
                    }
                />
                <div className={styles.content}>
                    <Text>
                        There was a problem completing the admin consent process for your organization.
                    </Text>

                    {error && (
                        <div>
                            <Text weight="semibold">Error code: </Text>
                            <span className={styles.errorCode}>{error}</span>
                        </div>
                    )}

                    {errorDescription && (
                        <div className={styles.errorDescription}>
                            <Text>{decodeURIComponent(errorDescription)}</Text>
                        </div>
                    )}

                    <Text className={styles.helpText}>
                        If you continue to experience issues, please contact your IT administrator
                        or ensure you have the required permissions to grant admin consent.
                    </Text>

                    <div className={styles.buttonContainer}>
                        <Button appearance="primary" onClick={handleTryAgain}>
                            Try Again
                        </Button>
                    </div>
                </div>
            </Card>
        </div>
    );
}
